export class PendoTracker {
  /**
   * Class instantiation constructor, sets defaults and
   * initializes Pendo
   * @param {Object} pendoConfig - Pendo api_key and enabled flag
   * @param {'dev'|'cert'|'prod'} environment - Server hosting environment
   * @param {function} [onloadFn] - optional callback for when the pendo script is done loading
   */
  constructor(pendoConfig = {}, environment = 'dev', onloadFn) {
    this.hasInitialized = false;
    this.config = pendoConfig;
    this.environment = environment;
    this.isProd = environment === 'prod';

    if (this.config.enabled && this.config.api_key) {
      this._loadScript(onloadFn);
    }
  }

  /**
   * Determines if we need to initilize the user or just update the existing user
   * @param {Object} user
   * @param {Object} team
   * @param {String} teamType
   */
  /* eslint-disable no-unused-vars */
  identify(user, team, teamType) {
    if (this.hasInitialized) {
      this.update(...arguments);
    } else {
      this.setup(...arguments);
    }
  }
  /* eslint-enable no-unused-vars */

  /**
   * Tell Pendo about the known authenticated user.
   * @param {Object} user
   * @param {Object} team
   * @param {String} teamType
   */
  setup(user, team, teamType) {
    const meta = this.formatIdentityData(user, team, teamType);
    if (meta) {
      this.pendo.initialize(meta);
      this.hasInitialized = true;
    }
  }

  /**
   * Update Pendo about team changes and reload all the pendo guides.
   * @param {Object} user
   * @param {Object} team
   * @param {String} teamType
   */
  update(user, team, teamType) {
    const meta = this.formatIdentityData(user, team, teamType);
    if (meta) {
      this.pendo.identify(meta);
      this.pendo.loadGuides();
    }
  }

  /**
   * Format user and team data into the format that pendo expects
   * @param {Object} user
   * @param {Object} team
   * @param {String} teamType
   * @returns {Object|Undefined}
   */
  formatIdentityData(user = {}, team = {}, teamType = 'org') {
    if (!this.pendo || !user || !user.id) {
      return;
    }

    let meta = {
      visitor: {
        id: this.isProd ? user.id : `${this.environment}_${user.id}`,
        name: user.name,
      },
    };

    if (team && team.id) {
      meta.account = {
        id: `console_${team.id}`,
        name: team.name,
        team_type: teamType,
        region: team.region,
      };
    }

    return meta;
  }

  /**
   * Insert the Pendo load script into the DOM
   * @param {string} apiKey - Pendo API Key
   * @param {function} [onloadFn] - optional callback when the script is done loading
   */
  _loadScript(onloadFn) {
    let pendo = (window.pendo = window.pendo || {});
    pendo._q = pendo._q || [];

    let methods = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
    let w, x;
    for (w = 0, x = methods.length; w < x; ++w) {
      (function (m) {
        pendo[m] =
          pendo[m] ||
          function () {
            pendo._q[m === methods[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
          };
      })(methods[w]);
    }

    // store Pendo global on this object
    this.pendo = pendo;

    // create the script html
    let script = document.createElement('script');
    script.async = !0;
    script.src = `https://cdn.pendo.io/agent/static/${this.config.api_key}/pendo.js`;

    // insert it before the first script tag on the page
    const el = document.getElementsByTagName('script')[0];
    el.parentNode.insertBefore(script, el);

    if (onloadFn) {
      script.onload = onloadFn;
    }
  }
}
